import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Typography,
} from '@mui/material';
import billService, { GenerateMonthlyBillsData } from '../services/billService';

interface GenerateBillsDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const GenerateBillsDialog: React.FC<GenerateBillsDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const currentDate = new Date();
  const [formData, setFormData] = useState<GenerateMonthlyBillsData>({
    month: currentDate.getMonth() + 1,
    year: currentDate.getFullYear(),
  });

  const months = [
    { value: 1, label: 'Januari' },
    { value: 2, label: 'Februari' },
    { value: 3, label: 'Maret' },
    { value: 4, label: 'April' },
    { value: 5, label: 'Mei' },
    { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' },
    { value: 8, label: 'Agustus' },
    { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' },
    { value: 12, label: 'Desember' },
  ];

  const handleInputChange = (field: keyof GenerateMonthlyBillsData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      const response = await billService.generateMonthlyBills(formData);
      const createdCount =
        response.data?.summary?.bills_created ??
        response.data?.generated_count ??
        0;
      const skippedCount = response.data?.summary?.bills_skipped ?? 0;

      setSuccess(
        `Berhasil generate ${createdCount} tagihan untuk ${months.find(m => m.value === formData.month)?.label} ${formData.year}` +
          (skippedCount > 0 ? ` (${skippedCount} dilewati)` : '')
      );
      
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate bills');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate Tagihan Bulanan</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <Alert severity="info">
              Generate tagihan bulanan akan membuat tagihan baru untuk semua customer aktif 
              yang memiliki paket pada periode yang dipilih. Tagihan yang sudah ada tidak akan dibuat ulang.
            </Alert>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <FormControl fullWidth required>
                <InputLabel>Bulan</InputLabel>
                <Select
                  value={formData.month}
                  label="Bulan"
                  onChange={(e) => handleInputChange('month', e.target.value as number)}
                >
                  {months.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <TextField
                fullWidth
                label="Tahun"
                type="number"
                value={formData.year}
                onChange={(e) => handleInputChange('year', parseInt(e.target.value) || currentDate.getFullYear())}
                required
                InputProps={{
                  inputProps: { 
                    min: 2020, 
                    max: currentDate.getFullYear() + 5 
                  }
                }}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Preview Generate
            </Typography>
            <Typography variant="body2">
              Periode: <strong>{months.find(m => m.value === formData.month)?.label} {formData.year}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Sistem akan mencari semua customer aktif yang memiliki tanggal aktivasi 
              dan generate tagihan sesuai dengan tanggal aktivasi masing-masing customer.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Batal
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !!success}
          >
            {loading ? 'Memproses...' : 'Generate Tagihan'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default GenerateBillsDialog;
